import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../config";

// Trophy-shipping report — password-gated tabular view of all 17 winners and
// their submitted contact / payment details. Shared link for the shipping team.
//
// Password ("kk26") is checked server-side by the SECURITY DEFINER RPC
// `jeedigunta_admin_winner_contacts`. Anyone with the password can read the
// data — there's no per-user auth, this is low-stakes obscurity for an
// internal helper page.

const PASSWORD_STORAGE_KEY = "jkkp_trophy_report_pwd";

const CATEGORY_LABELS = {
  galakala: { te: "గళకళ", en: "Voice Art" },
  kalam_galam: { te: "కలం-గళం", en: "Writer-Voice" },
  toli_kathanam_galam: { te: "తొలి కథనం - గళం", en: "Debut Voice" },
  toli_kathanam_kalam: { te: "తొలి కథనం - కలం", en: "Debut Writer-Voice" },
};

const CATEGORY_ORDER = [
  "galakala",
  "kalam_galam",
  "toli_kathanam_galam",
  "toli_kathanam_kalam",
];

const RANK_TE = { 1: "ప్రథమ", 2: "ద్వితీయ", 3: "తృతీయ" };

const formatAddress = (r) =>
  [r.address_line1, r.address_line2, r.city, r.state, r.postal_code, r.country]
    .filter((s) => s && String(s).trim() && s !== "-")
    .join(", ");

const formatPayout = (r) => {
  if (!r.payout_choice) return "—";
  if (r.payout_choice === "donate_sritaru") return "Donate to Sri Taru 🌱";
  if (r.payout_choice === "other")
    return `Other: ${r.payout_other_detail || "—"}`;
  const handle = r.payout_handle ? ` — ${r.payout_handle}` : "";
  return `${r.payout_choice.toUpperCase()}${handle}`;
};

const formatDonation = (r) => {
  if (!r.addon_donation_amount) {
    if (r.donation_message) return `(no amount) ${r.donation_message}`;
    return "—";
  }
  const amt = `${r.addon_donation_currency || ""} ${Number(
    r.addon_donation_amount
  ).toLocaleString()}`.trim();
  return r.donation_message ? `${amt} · ${r.donation_message}` : amt;
};

const CopyBtn = ({ text, label = "Copy" }) => {
  const [copied, setCopied] = useState(false);
  if (!text || text === "—") return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch (_e) {
          /* noop */
        }
      }}
      className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 align-middle"
      title="Copy to clipboard"
    >
      {copied ? "✓" : label}
    </button>
  );
};

const JeedigntaTrophyReport = () => {
  const [password, setPassword] = useState(
    () => sessionStorage.getItem(PASSWORD_STORAGE_KEY) || ""
  );
  const [pwInput, setPwInput] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "JKKP 2026 — Trophy Shipping Report";
  }, []);

  useEffect(() => {
    if (!password) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const { data, error: rpcError } = await supabase.rpc(
        "jeedigunta_admin_winner_contacts",
        { p_password: password }
      );
      if (cancelled) return;
      setLoading(false);
      if (rpcError) {
        setError("Wrong password.");
        sessionStorage.removeItem(PASSWORD_STORAGE_KEY);
        setPassword("");
        return;
      }
      setRows(data || []);
      sessionStorage.setItem(PASSWORD_STORAGE_KEY, password);
    })();
    return () => {
      cancelled = true;
    };
  }, [password]);

  const summary = useMemo(() => {
    const submitted = rows.filter((r) => r.token_status === "submitted").length;
    return { total: rows.length, submitted, pending: rows.length - submitted };
  }, [rows]);

  const grouped = useMemo(() => {
    const m = {};
    CATEGORY_ORDER.forEach((c) => (m[c] = { podium: [], jury: [] }));
    rows.forEach((r) => {
      const bucket = m[r.category] || (m[r.category] = { podium: [], jury: [] });
      if (r.is_special_jury) bucket.jury.push(r);
      else bucket.podium.push(r);
    });
    return m;
  }, [rows]);

  // ── Password gate ─────────────────────────────────────────────────────
  if (!password) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!pwInput.trim()) return;
            setPassword(pwInput.trim());
          }}
          className="w-full max-w-sm bg-slate-800 border border-amber-500/30 rounded-xl p-6 shadow-2xl"
        >
          <h1 className="text-amber-400 font-bold text-lg mb-1">
            🏆 Trophy Shipping Report
          </h1>
          <p className="text-slate-300 text-sm mb-5">
            Jeedigunta Kathakala Puraskaralu 2026 — winner contact &amp; payout
            details. Enter the password to continue.
          </p>
          <input
            type="password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Password"
            className="w-full bg-slate-900 border border-slate-600 focus:border-amber-400 rounded px-3 py-2 text-slate-100 outline-none"
            autoFocus
          />
          {error && (
            <p className="text-red-400 text-xs mt-2">{error}</p>
          )}
          <button
            type="submit"
            className="mt-4 w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-2 rounded transition-colors"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  // ── Loading state (after password accepted) ───────────────────────────
  if (loading && rows.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-600">
        Loading…
      </div>
    );
  }

  // ── Report ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        {/* Header */}
        <header className="mb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">
              🏆 JKKP 2026 — Trophy Shipping Report
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Jeedigunta Kathakala Puraskaralu 2026 · 17 winners · Sorted by
              category &amp; rank
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-800 border border-emerald-300">
              ✓ Submitted: {summary.submitted}
            </span>
            <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800 border border-amber-300">
              ⏳ Pending: {summary.pending}
            </span>
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem(PASSWORD_STORAGE_KEY);
                setPassword("");
              }}
              className="ml-2 text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100"
            >
              Lock
            </button>
          </div>
        </header>

        {/* Tables — one per category */}
        {CATEGORY_ORDER.map((cat) => {
          const bucket = grouped[cat] || { podium: [], jury: [] };
          const all = [
            ...bucket.podium.sort((a, b) => (a.rank || 99) - (b.rank || 99)),
            ...bucket.jury,
          ];
          if (all.length === 0) return null;
          const lbl = CATEGORY_LABELS[cat] || { te: cat, en: cat };
          return (
            <section
              key={cat}
              className="mb-6 bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden"
            >
              <h2 className="bg-slate-100 px-4 py-2.5 border-b border-slate-200 font-semibold text-slate-800">
                <span className="font-krushna">{lbl.te}</span>{" "}
                <span className="text-slate-500 font-normal">· {lbl.en}</span>
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 w-10">#</th>
                      <th className="px-3 py-2 w-28">Status</th>
                      <th className="px-3 py-2 w-56">Recipient</th>
                      <th className="px-3 py-2 w-44">Phone / Email</th>
                      <th className="px-3 py-2">Address</th>
                      <th className="px-3 py-2 w-56">Payout</th>
                      <th className="px-3 py-2 w-44">Donation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {all.map((r) => {
                      const isSubmitted = r.token_status === "submitted";
                      const rankLabel = r.is_special_jury
                        ? "⭐ Special Jury"
                        : `${RANK_TE[r.rank] || r.rank || "?"} · #${r.rank || "?"}`;
                      const fullAddr = formatAddress(r);
                      const payout = formatPayout(r);
                      const donation = formatDonation(r);
                      return (
                        <tr
                          key={r.contact_id}
                          className={`border-t border-slate-200 align-top ${
                            isSubmitted ? "" : "bg-amber-50/40"
                          }`}
                        >
                          <td className="px-3 py-3 text-slate-500 font-mono text-xs">
                            {r.token}
                          </td>
                          <td className="px-3 py-3">
                            {isSubmitted ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-800 border border-emerald-300">
                                ✓ Submitted
                              </span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 border border-amber-300">
                                ⏳ Pending
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <div className="font-krushna text-slate-900 text-base leading-snug">
                              {r.recipient_name}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {rankLabel}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs">
                            {isSubmitted ? (
                              <>
                                <div className="font-mono">
                                  {r.phone || "—"}
                                  <CopyBtn text={r.phone} />
                                </div>
                                <div className="text-slate-600 break-all mt-0.5">
                                  {r.email || "—"}
                                  <CopyBtn text={r.email} />
                                </div>
                              </>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-xs leading-relaxed">
                            {isSubmitted && fullAddr ? (
                              <>
                                <div className="whitespace-pre-line">
                                  {[
                                    r.address_line1,
                                    r.address_line2,
                                    [r.city, r.state, r.postal_code]
                                      .filter(Boolean)
                                      .join(", "),
                                    r.country,
                                  ]
                                    .filter((s) => s && s !== "-")
                                    .join("\n")}
                                </div>
                                <CopyBtn text={fullAddr} label="Copy address" />
                              </>
                            ) : (
                              <span className="text-slate-400">
                                Pending — share form link:{" "}
                                <span className="font-mono">
                                  /jkkp-form.html?t={r.token}
                                </span>
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-xs">
                            {isSubmitted ? (
                              <>
                                <div>{payout}</div>
                                {r.payout_handle && (
                                  <CopyBtn text={r.payout_handle} />
                                )}
                              </>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-xs text-slate-700">
                            {isSubmitted ? donation : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}

        <footer className="text-xs text-slate-500 mt-6 mb-12">
          Generated from <code>jeedigunta_winner_contacts</code> · refresh the
          page to pull the latest submissions.
        </footer>
      </div>
    </div>
  );
};

export default JeedigntaTrophyReport;
